import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Modal, Form, Input, Select, message, Tag, Popconfirm, Switch, Checkbox } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../services/api';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useAuth } from '../contexts/AuthContext';
import { trUpperNormalize } from '../utils/trLocale';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  isActive: boolean;
  isEmailVerified: boolean;
  roleCount: number;
  claimCount: number;
  createdAt: string;
  defaultWarehouseId?: number;
  canDelete: boolean;
  canCancelServiceOrder?: boolean;
}

interface Warehouse {
  id: number;
  warehouseCode: string;
  warehouseName: string;
}

interface Role {
  id: number;
  roleCode: string;
  roleName: string;
  description?: string;
}

/** Telefonu ekranda "532 000 00 00" formatında gösterir */
function formatPhoneDisplay(phone: string | undefined): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  if (digits.length <= 8) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
}

/** Maskeli input; form değeri sadece rakam (5320000000) olarak tutulur */
const PhoneInput: React.FC<{ value?: string; onChange?: (v: string) => void; placeholder?: string }> = ({ value, onChange, placeholder }) => (
  <Input
    placeholder={placeholder ?? '532 000 00 00'}
    value={formatPhoneDisplay(value)}
    onChange={(e) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
      onChange?.(digits);
    }}
  />
);

const Users: React.FC = () => {
  const isMobile = useBreakpoint();
  const { user: currentUser } = useAuth();
  const canDelete = currentUser?.canDelete !== false;
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadUsers();
    loadRoles();
    loadWarehouses();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/users');
      setUsers(data);
    } catch (error) {
      message.error('Kullanıcılar yüklenirken hata oluştu');
      console.error('Users load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const { data } = await api.get('/users/roles');
      setRoles(data);
    } catch (error) {
      console.error('Roles load error:', error);
    }
  };

  const loadWarehouses = async () => {
    try {
      const { data } = await api.get('/warehouses');
      setWarehouses(data);
    } catch (error) {
      console.error('Warehouses load error:', error);
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = async (user: User) => {
    try {
      const { data } = await api.get(`/users/${user.id}`);
      setEditingUser(user);
      form.setFieldsValue({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone ? String(data.phone).replace(/\D/g, '') : undefined,
        defaultWarehouseId: data.defaultWarehouseId,
        roleId: data.roles?.[0]?.roleId,
        canDelete: data.canDelete !== false,
        canCancelServiceOrder: data.canCancelServiceOrder === true
      });
      setModalVisible(true);
    } catch (error) {
      message.error('Kullanıcı bilgileri yüklenirken hata oluştu');
      console.error('User load error:', error);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await api.delete(`/users/${id}`);
      message.success('Kullanıcı silindi');
      loadUsers();
    } catch (error) {
      message.error('Kullanıcı silinirken hata oluştu');
      console.error('Delete error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id: number, currentStatus: boolean) => {
    try {
      await api.patch(`/users/${id}/toggle-active`);
      message.success(`Kullanıcı ${!currentStatus ? 'aktif' : 'pasif'} edildi`);
      loadUsers();
    } catch (error) {
      message.error('Durum değiştirilirken hata oluştu');
      console.error('Toggle active error:', error);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const phoneForDb = values.phone ? String(values.phone).replace(/\D/g, '') : undefined;
      const trUpper = (s: string | undefined) => (s != null && s !== '' ? String(s).toLocaleUpperCase('tr-TR') : s);
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, {
          email: values.email,
          firstName: trUpper(values.firstName),
          lastName: trUpper(values.lastName),
          phone: phoneForDb,
          password: values.password || undefined,
          defaultWarehouseId: values.defaultWarehouseId,
          roleId: values.roleId ?? null,
          canDelete: !!values.canDelete,
          canCancelServiceOrder: !!values.canCancelServiceOrder
        });
        message.success('Kullanıcı güncellendi');
      } else {
        await api.post('/users', {
          email: values.email,
          password: values.password,
          firstName: trUpper(values.firstName),
          lastName: trUpper(values.lastName),
          phone: phoneForDb,
          defaultWarehouseId: values.defaultWarehouseId,
          roleId: values.roleId ?? null,
          canDelete: !!values.canDelete,
          canCancelServiceOrder: !!values.canCancelServiceOrder
        });
        message.success('Kullanıcı eklendi');
      }
      setModalVisible(false);
      form.resetFields();
      loadUsers();
    } catch (error) {
      message.error('Kullanıcı kaydedilirken hata oluştu');
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: 'Ad Soyad',
      key: 'fullName',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
      render: (text: string) => (text ? formatPhoneDisplay(text) : '-')
    },
    {
      title: 'Rol',
      dataIndex: 'roleCount',
      key: 'roleCount',
      width: 120,
      render: (count: number) => (
        <Tag color="blue">
          <UserOutlined /> {count > 0 ? count : '-'}
        </Tag>
      ),
    },
    {
      title: 'Yetkiler',
      dataIndex: 'claimCount',
      key: 'claimCount',
      width: 100,
      render: (count: number) => (
        <Tag color="cyan">{count}</Tag>
      ),
    },
    {
      title: 'E-posta Doğrulandı',
      dataIndex: 'isEmailVerified',
      key: 'isEmailVerified',
      width: 120,
      render: (verified: boolean) => (
        <Tag color={verified ? 'green' : 'orange'}>
          {verified ? 'Evet' : 'Hayır'}
        </Tag>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean, record: User) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleActive(record.id, isActive)}
          checkedChildren="Aktif"
          unCheckedChildren="Pasif"
        />
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          {canDelete && (
            <Popconfirm
              title="Bu kullanıcıyı silmek istediğinizden emin misiniz?"
              onConfirm={() => handleDelete(record.id)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Kullanıcılar"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Yeni Kullanıcı
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        scroll={{ x: 700 }}
        pagination={{
          pageSize: 10,
          showSizeChanger: !isMobile,
          showQuickJumper: !isMobile,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
        }}
      />

      <Modal
        title={editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 600}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="firstName"
            label="Ad"
            normalize={trUpperNormalize}
            rules={[{ required: true, message: 'Ad giriniz' }]}
          >
            <Input placeholder="Ad" />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Soyad"
            normalize={trUpperNormalize}
            rules={[{ required: true, message: 'Soyad giriniz' }]}
          >
            <Input placeholder="Soyad" />
          </Form.Item>

          <Form.Item
            name="email"
            label="E-posta (opsiyonel)"
            rules={[{ type: 'email', message: 'Geçerli bir e-posta adresi giriniz' }]}
          >
            <Input placeholder="E-posta adresi" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Telefon"
            rules={[{ required: true, message: 'Telefon giriniz' }]}
          >
            <PhoneInput />
          </Form.Item>

          <Form.Item
            name="defaultWarehouseId"
            label="Varsayılan Depo"
            rules={[{ required: true, message: 'Varsayılan depo seçiniz' }]}
          >
            <Select
              placeholder="Depo seçin"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.children ?? '').toLocaleLowerCase('tr-TR').includes(input.toLocaleLowerCase('tr-TR'))
              }
            >
              {warehouses.map(warehouse => (
                <Select.Option key={warehouse.id} value={warehouse.id}>
                  {warehouse.warehouseCode} - {warehouse.warehouseName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="password"
            label={editingUser ? "Yeni Şifre (boş bırakılırsa değişmez)" : "Şifre"}
            rules={editingUser ? [] : [{ required: true, message: 'Şifre giriniz' }]}
          >
            <Input.Password placeholder="Şifre" />
          </Form.Item>

          <Form.Item
            name="roleId"
            label="Rol"
          >
            <Select
              placeholder="Rol seçin"
              allowClear
              options={roles.map(role => ({ label: role.roleName, value: role.id }))}
            />
          </Form.Item>

          <Form.Item
            name="canDelete"
            label="Silme İzni"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox>Silme izni (açıksa kullanıcı tüm sayfalarda Sil butonunu görür)</Checkbox>
          </Form.Item>

          <Form.Item
            name="canCancelServiceOrder"
            label="İptal İzni"
            valuePropName="checked"
            initialValue={false}
          >
            <Checkbox>Servis fişi iptal (geri alma ve kopya) yetkisi</Checkbox>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingUser ? 'Güncelle' : 'Kaydet'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Users;
